import { createFeatureSelector, createSelector } from "@ngrx/store";
import { AutomationHardwareItem, AutomationVendorHardwareData, ClientDevice, EditProcedureState, EngineeringUpdateMeeting, EngineeringUpdateMeetingUserContent, FactoryNode, FactoryNodeConfig, KnowledgeDocument, LabelPrinter, LabelPrinterGroup, Procedure, ProcedureInstance, ProcedureStepRedline, ProcedureStepRedlineStatus, ProcedureVersion, ProcedureVersionStatus, RobotProgram, RobotProgramGroup, SaveStatus, SoftwareTask, SoftwareTaskStatus, Tag, toEntityDict, UserTimeSession, ViewProcedureState } from "@prism/prism-shared";
import { AutomationState } from "./automation.reducer";

export const automationState = createFeatureSelector<AutomationState>('automation');

export const getLoadingState = createSelector(
  automationState,
  (state: AutomationState): number => state.loadingState
);

export const getError = createSelector(
  automationState,
  (state: AutomationState): any => state.error
);

export const getFactoryNodes = createSelector(
  automationState,
  (state: AutomationState): FactoryNode[] => state.factoryNodes
);

export const getFactoryNodeConfigs = createSelector(
  automationState,
  (state: AutomationState): FactoryNodeConfig[] => state.factoryNodeConfigs
);

export const getRobotProgramGroups = createSelector(
  automationState,
  (state: AutomationState): RobotProgramGroup[] => state.robotProgramGroups
);

export const getRobotPrograms = createSelector(
  automationState,
  (state: AutomationState): RobotProgram[] => state.robotPrograms
);

export const getTags = createSelector(
  automationState,
  (state: AutomationState): Tag[] => state.tags
);

export const getClientDevices = createSelector(
  automationState,
  (state: AutomationState): ClientDevice[] => state.clientDevices
);

export const getUserTimeSessions = createSelector(
  automationState,
  (state: AutomationState): UserTimeSession[] => state.userTimeSessions
)

export const getSoftwareTasks = createSelector(
  automationState,
  (state: AutomationState): SoftwareTask[] => state.softwareTasks
)

export const getLabelPrinters = createSelector(
  automationState,
  (state: AutomationState): LabelPrinter[] => state.labelPrinters
)

export const getLabelPrinterGroups = createSelector(
  automationState,
  (state: AutomationState): LabelPrinterGroup[] => state.labelPrinterGroups
)

export const getEngineeringUpdateMeetings = createSelector(
  automationState,
  (state: AutomationState): EngineeringUpdateMeeting[] => state.engineeringUpdateMeetings
)

export const getEngineeringUpdateMeetingUserContents = createSelector(
  automationState,
  (state: AutomationState): EngineeringUpdateMeetingUserContent[] => state.engineeringUpdateMeetingUserContents
)

export const getKnowledgeDocuments = createSelector(
  automationState,
  (state: AutomationState): KnowledgeDocument[] => state.knowledgeDocuments
)

export const getAutomationHardwareItems = createSelector(
  automationState,
  (state: AutomationState): AutomationHardwareItem[] => state.automationHardwareItems
)

export const getAutomationHardwareItemDict = createSelector(
  getAutomationHardwareItems,
  (items: AutomationHardwareItem[]): Record<string, AutomationHardwareItem> => toEntityDict<AutomationHardwareItem>(items || [])
)

export function getAutomationHardwareItemById(itemId: string) {
  return createSelector(
    getAutomationHardwareItems,
    (items: AutomationHardwareItem[]): AutomationHardwareItem => items.find(i => i.id === itemId) as AutomationHardwareItem
  )
}

export const getAutomationVendorHardwareData = createSelector(
  automationState,
  (state: AutomationState): AutomationVendorHardwareData[] => state.automationVendorHardwareData
)

export function getAutomationVendorHardwareDataByHardwareItemId(hardwareItemId: string) {
  return createSelector(
    getAutomationVendorHardwareData,
    (data: AutomationVendorHardwareData[]): AutomationVendorHardwareData[] => data.filter(d => d.parentHardwareItemId === hardwareItemId)
  )
}

// ===============================================================================  

export const getTagsDict = createSelector(
  getTags,
  (tags: Tag[]): Record<string, Tag> => toEntityDict<Tag>(tags || [])
);


export function getFactoryNodeById(factoryNodeId: string) {
  return createSelector(
    getFactoryNodes,
    (factoryNodes: FactoryNode[]): FactoryNode => {
      return factoryNodes.find((factoryNode: FactoryNode) => factoryNode.id === factoryNodeId) as FactoryNode;
    }
  );
}


export function getFactoryNodeConfigsByFactoryNodeId(factoryNodeId: string) {
  return createSelector(
    getFactoryNodeConfigs,
    (factoryNodeConfigs: FactoryNodeConfig[]) => {
      return factoryNodeConfigs.filter((factoryNodeConfig: FactoryNodeConfig) => factoryNodeConfig.factoryNodeId === factoryNodeId);
    }
  );
}

export const getFactoryNodeConfigsDict = createSelector(
  getFactoryNodeConfigs,
  (factoryNodeConfigs: FactoryNodeConfig[]): Record<string, FactoryNodeConfig> => {
    return factoryNodeConfigs.reduce((acc, factoryNodeConfig) => {
      // @ts-ignore
      acc[factoryNodeConfig.id] = factoryNodeConfig;
      return acc;
    }, {});
  }
);

export const getReleasedFactoryNodeConfigsDictByFactoryNodeId = createSelector(
  getFactoryNodes,
  getFactoryNodeConfigs,
  (factoryNodes: FactoryNode[], factoryNodeConfigs: FactoryNodeConfig[]): Record<string, FactoryNodeConfig> => {
    return factoryNodeConfigs.reduce((acc, factoryNodeConfig) => {
      const factoryNode = factoryNodes.find((factoryNode: FactoryNode) => factoryNode.id === factoryNodeConfig.factoryNodeId);
      if (factoryNode && factoryNode.deployedFactoryNodeConfigId === factoryNodeConfig.id) {
        // @ts-ignore
        acc[factoryNodeConfig.id] = factoryNodeConfig;
      }
      return acc;
    }, {});
  }
);



export function getFactoryNodeConfigsDictByFactoryNodeId(factoryNodeId: string) {
  return createSelector(
    getFactoryNodeConfigs,
    (factoryNodeConfigs: FactoryNodeConfig[]): Record<string, FactoryNodeConfig> => {
      return factoryNodeConfigs.reduce((acc, factoryNodeConfig) => {
        if (factoryNodeConfig.factoryNodeId === factoryNodeId) {
          // @ts-ignore
          acc[factoryNodeConfig.id] = factoryNodeConfig;
        }
        return acc;
      }, {});
    }
  );
}


export function getFactoryNodeConfigById(factoryNodeConfigId: string) {
  return createSelector(
    getFactoryNodeConfigs,
    (factoryNodeConfigs: FactoryNodeConfig[]) => {
      return factoryNodeConfigs.find((factoryNodeConfig: FactoryNodeConfig) => factoryNodeConfig.id === factoryNodeConfigId);
    }
  );
}

export function getRobotProgramGroupById(groupId: string) {
  return createSelector(
    getRobotProgramGroups,
    (groups: RobotProgramGroup[]): RobotProgramGroup => {
      return groups.find((group: RobotProgramGroup) => group.id === groupId) as RobotProgramGroup;
    }
  );
}

export function getRobotProgramsByRobotProgramGroupId(groupId: string) {
  return createSelector(
    getRobotPrograms,
    (programs: RobotProgram[]) => {
      return programs.filter((program: RobotProgram) => program.programGroupId === groupId);
    }
  );
}

export function getUserTimeSessionsByIds(userTimeSessionIds: string[]) {
  return createSelector(
    getUserTimeSessions,
    (timeSessions: UserTimeSession[]): UserTimeSession[] => timeSessions.filter((t) => userTimeSessionIds.includes(t.id))
  )
}

export const getIncompleteSoftwareTasks = createSelector(
  getSoftwareTasks,
  (softwareTasks: SoftwareTask[]): SoftwareTask[] => softwareTasks.filter((t) => t.status !== SoftwareTaskStatus.Complete)
)

export const getCompleteSoftwareTasks = createSelector(
  getSoftwareTasks,
  (softwareTasks: SoftwareTask[]): SoftwareTask[] => softwareTasks.filter((t) => t.status === SoftwareTaskStatus.Complete)
)

export function getEngineeringUpdateMeetingById(meetingId: string) {
  return createSelector(
    getEngineeringUpdateMeetings,
    (meetings: EngineeringUpdateMeeting[]): EngineeringUpdateMeeting => meetings.find(m => m.id === meetingId) as EngineeringUpdateMeeting
  )
}

export function getEngineeringUpdateMeetingUserContentsByMeetingId(meetingId: string) {
  return createSelector(
    getEngineeringUpdateMeetingUserContents,
    (contents: EngineeringUpdateMeetingUserContent[]): EngineeringUpdateMeetingUserContent[] => contents.filter(c => c.meetingId === meetingId)
  )
} 
